import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatQuotaExhaustedNotice } from '../utils/quotaExhaustedNotice';
import { toSpokenText } from '../utils/spokenText';
import { getToolMessageKey, isDisplayedToolEvent } from '../utils/toolMessages';

import type { ConversationMode } from '../constants/modes';
import type { ToolStatus } from '../utils/toolMessages';

export type AnnouncedMessage = {
  id: string | number;
  sender: 'user' | 'bot';
  text: string;
  isError?: boolean;
  isStreaming?: boolean;
};

export type AnnouncedToolEvent = {
  id?: string;
  toolName?: string;
  toolType?: string;
  status?: ToolStatus;
  timestamp?: string;
};

export type UseTutorAnnouncementParams = {
  activeToolEvents: AnnouncedToolEvent[];
  conversationId: string | null;
  isQuotaExhausted?: boolean;
  isVisible: boolean;
  messages: AnnouncedMessage[];
  mode: ConversationMode;
  streamingMessage: AnnouncedMessage | null;
};

type Turn = {
  announcedToolKeys: Set<string>;
  baselineIds: Set<AnnouncedMessage['id']>;
};

type Translate = (key: string, options?: Record<string, unknown>) => string;

// i18next 19 vuelve a interpolar el valor de una variable: una respuesta con `{{…}}` (código,
// LaTeX) se recortaría y una con `$t(…)` agotaría la pila. La respuesta no pasa por `t`.
const ANSWER_PLACEHOLDER = '__TUTOR_ANSWER__';

function toolKey(event: AnnouncedToolEvent): string {
  return `${event.id ?? event.toolName}:${event.status}`;
}

export function formatTutorSays(t: Translate, spokenText: string): string {
  return t('chatbot:chat_tutor_says_announcement', { text: ANSWER_PLACEHOLDER }).replace(ANSWER_PLACEHOLDER, () => spokenText);
}

type QuotaChange = { hasRunOut: boolean; isExhausted: boolean; notice: string };

// `null` mientras el turno no ha terminado: `handleComplete` quita la burbuja y añade el mensaje en dos
// actualizaciones separadas, así que la respuesta llega en el render siguiente.
function getTurnEndAnnouncement(t: Translate, turn: Turn, messages: AnnouncedMessage[], quota: QuotaChange): string | null {
  const currentIds = new Set(messages.map((message) => message.id));
  const isAppendOnly = Array.from(turn.baselineIds).every((id) => currentIds.has(id));
  const newMessages = messages.filter((message) => !turn.baselineIds.has(message.id));
  const answer = [...newMessages].reverse().find((message) => message.sender === 'bot');

  if (isAppendOnly && answer) {
    const spokenText = toSpokenText(answer.text);
    // Una sola región: si la cuota se acabó en este turno, el aviso va detrás de la respuesta para que
    // el lector no lo diga antes que ella.
    return [spokenText ? formatTutorSays(t, spokenText) : '', quota.isExhausted ? quota.notice : ''].filter(Boolean).join(' ');
  }

  // El back rechazó el turno por falta de cupo: no llega respuesta y la cuota se agota después de
  // quitar la burbuja, al releerla. Si se agotase con la burbuja aún puesta, la respuesta llegaría en
  // el render siguiente.
  if (quota.hasRunOut) {
    return quota.notice;
  }

  // El turno terminó sin respuesta (cancelado, chat nuevo, otra conversación): se vacía la región
  // para que el «está escribiendo» del siguiente turno sea un cambio que el lector anuncie.
  if (!isAppendOnly || newMessages.length > 0) {
    return '';
  }

  return null;
}

/**
 * Qué debe oír un lector de pantalla del turno del tutor: que está escribiendo, la herramienta que
 * prepara, el error si falla y la respuesta completa en texto plano. Nunca los fragmentos del
 * streaming, que inundarían al lector, ni el historial que se carga al abrir una conversación.
 * También que el alumno se ha quedado sin interacciones, porque la caja se desactiva sin otra
 * explicación; no al abrir el chat ya agotado, cuando el mensaje es lo que se ve.
 */
function useTutorAnnouncement({
  activeToolEvents,
  conversationId,
  isQuotaExhausted = false,
  isVisible,
  messages,
  mode,
  streamingMessage,
}: UseTutorAnnouncementParams): string {
  const { t } = useTranslation(['chatbot']);
  const [announcement, setAnnouncement] = useState<string>('');
  const turnRef = useRef<Turn | null>(null);
  const previousConversationIdRef = useRef<string | null>(conversationId);
  const wasQuotaExhaustedRef = useRef<boolean>(isQuotaExhausted);

  useEffect(() => {
    const quota: QuotaChange = {
      hasRunOut: isQuotaExhausted && !wasQuotaExhaustedRef.current,
      isExhausted: isQuotaExhausted,
      notice: formatQuotaExhaustedNotice(t, mode),
    };
    wasQuotaExhaustedRef.current = isQuotaExhausted;

    // Al ocultar el chat el motor corta el stream sin respuesta: el turno se descarta para que el
    // siguiente se anuncie, aunque su mensaje y su burbuja lleguen en el mismo render.
    if (!isVisible) {
      turnRef.current = null;
      setAnnouncement('');
      return;
    }

    // Pasar de «sin conversación» a una nueva es el primer mensaje creándola: el turno sigue vivo.
    // Cambiar de una conversación existente a otra sí lo descarta.
    const previousConversationId = previousConversationIdRef.current;
    previousConversationIdRef.current = conversationId;
    if (previousConversationId !== null && previousConversationId !== conversationId && turnRef.current) {
      turnRef.current = null;
      setAnnouncement('');
      return;
    }

    if (streamingMessage?.isError) {
      turnRef.current = null;
      setAnnouncement(streamingMessage.text ?? '');
      return;
    }

    if (streamingMessage && !turnRef.current) {
      // Las herramientas que siguen en pantalla son del turno anterior (el motor no las limpia al
      // fallar ni al reintentar): se dan por anunciadas.
      turnRef.current = {
        announcedToolKeys: new Set(activeToolEvents.map(toolKey)),
        baselineIds: new Set(messages.map((message) => message.id)),
      };
      setAnnouncement(t('chatbot:chat_tutor_typing_announcement'));
    }

    const turn = turnRef.current;
    if (!turn) {
      if (quota.hasRunOut) setAnnouncement(quota.notice);
      return;
    }

    if (streamingMessage) {
      const newTool = activeToolEvents.find(
        (event) => isDisplayedToolEvent(event) && (event.status === 'started' || event.status === 'failed') && !turn.announcedToolKeys.has(toolKey(event))
      );
      if (newTool?.status) {
        turn.announcedToolKeys.add(toolKey(newTool));
        setAnnouncement(t(getToolMessageKey(newTool.status, newTool.timestamp)));
      }
      return;
    }

    const turnEndAnnouncement = getTurnEndAnnouncement(t, turn, messages, quota);
    if (turnEndAnnouncement !== null) {
      turnRef.current = null;
      setAnnouncement(turnEndAnnouncement);
    }
  }, [activeToolEvents, conversationId, isQuotaExhausted, isVisible, messages, mode, streamingMessage, t]);

  return announcement;
}

export default useTutorAnnouncement;
